import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query'
import { useMangoApiClient } from '~/atoms/MangoApi'
import { AsyncQueryCancellation } from '~/types/ApiRequestTypes'

import { ApiError, BlockedNumber } from '@mangovoice/types'

export interface BlockNumberResponse {
  id: number
  phone_number: string
  pbx_id: number
}

interface GetBlockedNumbersParams {
  pbxId: number | undefined
  options?: Omit<UseQueryOptions<BlockedNumber[], ApiError>, 'queryKey'>
  onSuccess: (pbxId: number, blockedNumbers: BlockedNumber[]) => void
  onError: (error: unknown) => void
}

// Get blocked numbers
export const useGetBlockedNumbersQuery = ({
  onSuccess,
  onError,
  options,
  pbxId,
}: GetBlockedNumbersParams) => {
  const api = useMangoApiClient()
  const queryClient = useQueryClient()

  const getQueryKeys = () => ['getBlockedNumbers', { pbxId }]

  const tryCancelQueries: AsyncQueryCancellation = async () => {
    await queryClient.cancelQueries({
      queryKey: getQueryKeys(),
    })
  }

  const { refetch: _refetch, data } = useQuery<BlockedNumber[], ApiError>({
    queryKey: [...getQueryKeys()],
    queryFn: async ({ signal }) => {
      if (!pbxId || pbxId === 0) {
        const error = new Error('pbxId is required')
        if (!signal?.aborted) {
          onError(error)
        }
        return Promise.reject(error)
      }

      const { data, ok, status } =
        await api.blockedNumbers.getBlockedNumbers(pbxId)

      // No HTTP status (nullish or 0) means the request never reached the
      // server — abort, offline, timeout, or cancel. These are transient and
      // out of our control, so they must not be reported to Sentry (the bulk
      // of "Failed to get blocked numbers (status: unknown)" noise). Only a
      // real HTTP error status below is worth surfacing.
      const noServerResponse = signal?.aborted || status == null || status === 0

      if (!ok) {
        if (noServerResponse) {
          return Promise.reject(new Error('Request aborted'))
        }

        const responseMessage =
          data &&
          typeof data === 'object' &&
          'message' in data &&
          typeof data.message === 'string'
            ? data.message
            : undefined
        const errorMessage =
          responseMessage ||
          `Failed to get blocked numbers (status: ${status ?? 'unknown'})`
        const error = new Error(errorMessage)
        Object.assign(error, {
          ...(data as object),
          status,
        })
        onError(error)
        return Promise.reject(error)
      }
      onSuccess(pbxId, data?.results ?? [])
      return data?.results ?? []
    },
    ...options,
    enabled: options?.enabled ?? false,
    retry: options?.retry ?? 1,
  })

  const refetch = async () => {
    await tryCancelQueries()
    await _refetch()
  }

  return {
    refetch,
    blockedNumbers: data ?? [],
  }
}

// Block
interface UseBlockNumberParams {
  pbxId: number | undefined
}

interface BlockHandlers {
  onSuccess?: (blockedNumber: BlockNumberResponse | undefined) => void
  onError?: (error: ApiError) => void
  onCompletion?: () => void
}
export const useBlockNumberQuery = ({ pbxId }: UseBlockNumberParams) => {
  const api = useMangoApiClient()

  const blockNumberMutation = useMutation({
    mutationFn: async (phoneNumber: string) => {
      if (!pbxId) {
        return Promise.reject('pbxId is required')
      }

      const { ok, data } = await api.blockedNumbers.blockNumber(
        pbxId,
        phoneNumber,
      )

      if (!ok) return Promise.reject('Failed to block number.')

      return data
    },
    onSuccess: (_: BlockNumberResponse | undefined) => {},
    onError: (_: ApiError) => {},
    retry: 1,
  })

  const blockNumberQuery = (
    phoneNumber: string,
    { onSuccess, onError, onCompletion }: BlockHandlers,
  ) => {
    blockNumberMutation.mutate(phoneNumber, {
      onSuccess,
      onError,
      onSettled: onCompletion,
    })
  }

  return blockNumberQuery
}

// Unblock
interface UnblockHandlers {
  onSuccess?: (unblocked: boolean) => void
  onError?: (error: ApiError) => void
  onCompletion?: () => void
}
export const useUnblockNumberQuery = () => {
  const api = useMangoApiClient()

  const unblockNumberMutation = useMutation({
    mutationFn: async (blockedNumberId: number) => {
      const { ok, status } =
        await api.blockedNumbers.unblockNumber(blockedNumberId)

      if (status === 404) {
        // 404 here means someone else has unblocked the number already, we need to reload blocked numbers
        return false
      }
      if (!ok) return Promise.reject('Failed to unblock number.')
      return true
    },
    onSuccess: (_: boolean) => {},
    onError: (_: ApiError) => {},
    retry: 1,
  })

  const unblockNumberQuery = (
    blockedNumberId: number,
    { onSuccess, onError, onCompletion }: UnblockHandlers,
  ) => {
    unblockNumberMutation.mutate(blockedNumberId, {
      onSuccess,
      onError,
      onSettled: onCompletion,
    })
  }

  return unblockNumberQuery
}
